import type { SVGProps } from 'react'

/**
 * ChooseChow logomark — a vector distillation of the official mascot (a tomato
 * chef in a white hat over a navy wok, on a crimson disc). The full raster
 * mascot lives in `public/brand/` and is used at 96px and up; this mark is for
 * in-app chrome (headers, nav, auth tile), where the mascot's spatula and
 * tomato would blur into a smudge.
 *
 * Paths match `SMALL`/`FULL_MARK` in scripts/brand/generate_icons.py. The disc
 * reads `--minimal-color-brand` so it restains with the edition theme; ink and
 * hat stay fixed because they are part of the character, not the accent.
 * Decorative by default; the adjacent wordmark carries the accessible name.
 */
export interface BrandMarkProps extends Omit<SVGProps<SVGSVGElement>, 'width' | 'height'> {
  size?: number
}

const INK = '#17273E'

export const BrandMark = ({ size = 32, ...props }: BrandMarkProps) => {
  // Below 24px the face and highlight merge into noise; drop them and thicken.
  const compact = size < 24
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      aria-hidden="true"
      focusable="false"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <circle cx="32" cy="30" r={compact ? 26 : 25} fill="var(--minimal-color-brand)" />
      {compact ? (
        <g stroke={INK} strokeWidth="3.6">
          <path
            d="M22 26c-5-.5-7.4-4-7-7.8.5-3.9 4.1-6.5 8-5.6C24.2 7.8 27.6 5 32 5s7.8 2.8 9 7.6c3.9-.9 7.5 1.7 8 5.6.4 3.8-2 7.3-7 7.8V32H22Z"
            fill="#fff"
          />
          <path d="M5 44h54" strokeWidth="4.5" />
          <path d="M10.5 44c1 9.5 9.5 15 21.5 15s20.5-5.5 21.5-15Z" fill={INK} />
        </g>
      ) : (
        <>
          <g stroke={INK} strokeWidth="2.2">
            {/* Head */}
            <circle cx="32" cy="36" r="10" fill="var(--minimal-color-brand)" />
            {/* Closed happy eyes + smile */}
            <path d="M28.6 33.6q1.2-1.3 2.4 0M33 33.6q1.2-1.3 2.4 0M29.2 37.6q2.8 2.6 5.6 0" strokeWidth="1.6" />
            {/* Chef hat */}
            <path
              d="M23 24.5c-4.6-.4-7-3.6-6.6-7.2.4-3.6 3.8-6 7.4-5.2C24.9 7.6 28 5 32 5s7.1 2.6 8.2 7.1c3.6-.8 7 1.6 7.4 5.2.4 3.6-2 6.8-6.6 7.2V29H23Z"
              fill="#fff"
            />
            {/* Wok rim + bowl */}
            <path d="M6.5 44.5h51" strokeWidth="3" />
            <path d="M11.5 44.5c1 8.8 9 13.5 20.5 13.5s19.5-4.7 20.5-13.5Z" fill={INK} />
          </g>
          <path d="M18 48.5c1.6 3 4.4 4.9 8 5.8" stroke="#fff" strokeOpacity=".55" strokeWidth="1.6" />
        </>
      )}
    </svg>
  )
}

/**
 * The full raster mascot (waving arm, spatula, tomato). Use at 96px and up —
 * auth, landing hero, empty states. Always decorative next to visible text.
 */
export const BrandMascot = ({ size = 160, className }: { size?: number; className?: string }) => (
  <picture>
    <source srcSet="/brand/choosechow-mascot.webp" type="image/webp" />
    <img
      src="/brand/choosechow-mascot.webp"
      width={size}
      height={size}
      alt=""
      aria-hidden="true"
      decoding="async"
      className={className}
    />
  </picture>
)
